import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_LAT, DEFAULT_LON, isDaylight, nextSwitch } from "@/lib/sun";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuth } from "@/hooks/useAuth";

export type ThemeMode = "auto" | "light" | "dark";

type Ctx = {
  mode: ThemeMode;
  /** Aktivt utseende just nu */
  resolved: "light" | "dark";
  setMode: (m: ThemeMode) => void;
};

const ThemeContext = createContext<Ctx>({ mode: "auto", resolved: "light", setMode: () => {} });
const STORAGE_KEY = "sellavo-theme-mode";
const COORD_KEY = "sellavo-home-coords";
// Äldre nycklar från när tjänsten hette något annat – läses fortfarande så ingen tappar sitt läge.
const LEGACY_STORAGE_KEY = "saljavo-theme-mode";
const LEGACY_COORD_KEY = "saljavo-home-coords";

function readCoords(): { lat: number; lon: number } {
  try {
    const raw = localStorage.getItem(COORD_KEY) ?? localStorage.getItem(LEGACY_COORD_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p?.lat === "number" && typeof p?.lon === "number")
        return { lat: p.lat, lon: p.lon };
    }
  } catch {
    /* ignore */
  }
  return { lat: DEFAULT_LAT, lon: DEFAULT_LON };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>("auto");
  const [resolved, setResolved] = useState<"light" | "dark">("light");
  // Kom ihåg vilket konto vi senast hämtade inställningen för
  const loadedForUser = useRef<string | null>(null);

  // Läs sparat läge efter hydrering (gäller även utloggade besökare)
  useEffect(() => {
    const saved = (localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem(LEGACY_STORAGE_KEY)) as ThemeMode | null;
    if (saved === "light" || saved === "dark" || saved === "auto") setModeState(saved);
  }, []);

  // Hämta kontots sparade utseende när man loggar in – kontot vinner över enheten
  useEffect(() => {
    if (!user) {
      loadedForUser.current = null;
      return;
    }
    if (loadedForUser.current === user.id) return;
    loadedForUser.current = user.id;
    let cancelled = false;
    (async () => {
      const supabase = await getSupabase();
      const { data } = await supabase
        .from("user_preferences")
        .select("theme_mode")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const remote = data?.theme_mode as ThemeMode | undefined;
      if (remote === "light" || remote === "dark" || remote === "auto") {
        localStorage.setItem(STORAGE_KEY, remote);
        setModeState(remote);
      } else {
        // Inget sparat på kontot ännu – spara enhetens nuvarande val
        const local = (localStorage.getItem(STORAGE_KEY) as ThemeMode | null) ?? "auto";
        await supabase.from("user_preferences").upsert({ user_id: user.id, theme_mode: local });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const apply = () => {
      let next: "light" | "dark";
      if (mode === "auto") {
        const { lat, lon } = readCoords();
        next = isDaylight(new Date(), lat, lon) ? "light" : "dark";
        const ms = Math.max(60000, nextSwitch(new Date(), lat, lon).getTime() - Date.now() + 1000);
        timer = setTimeout(apply, Math.min(ms, 6 * 3600000));
      } else {
        next = mode;
      }
      setResolved(next);
      document.documentElement.classList.toggle("dark", next === "dark");
      document.documentElement.style.colorScheme = next;
    };

    apply();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [mode]);

  const setMode = useCallback(
    (m: ThemeMode) => {
      setModeState(m);
      localStorage.setItem(STORAGE_KEY, m);
      if (user) {
        // .then() krävs – frågan skickas först när löftet konsumeras
        void getSupabase().then(async (sb) => {
          const { error } = await sb.from("user_preferences").upsert({ user_id: user.id, theme_mode: m });
          if (error) console.error("Kunde inte spara utseendet på kontot", error.message);
        });
      }
    },
    [user],
  );

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
